/**
 * Global formatting utilities for Cleanifax CRM
 * Consistent currency and phone number formatting across the app
 */

/**
 * Format currency values with locale en-CA
 * @param {number|string} value - The numeric value to format
 * @returns {string} Formatted currency string (e.g., $1,500.00)
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '$0.00';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return '$0.00';
  
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

/**
 * Format phone numbers to (###) ###-#### format
 * @param {string} value - The phone number to format (can be raw digits or partially formatted)
 * @returns {string} Formatted phone number string (e.g., (902) 555-1234)
 */
export const formatPhone = (value) => {
  if (!value) return '';
  
  // Remove all non-numeric characters
  const cleaned = value.toString().replace(/\D/g, '');
  
  // Handle different lengths
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // For numbers longer than 10 digits, format only the first 10
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

/**
 * Clean phone number to store in database (remove all formatting)
 * @param {string} value - The formatted phone number
 * @returns {string} Clean phone number with only digits
 */
export const cleanPhone = (value) => {
  if (!value) return '';
  return value.toString().replace(/\D/g, '');
};

/**
 * Parse currency string to number for calculations
 * @param {string} value - Formatted currency string
 * @returns {number} Numeric value
 */
export const parseCurrency = (value) => {
  if (!value) return 0;
  const cleaned = value.toString().replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Format number with commas (no currency symbol)
 * @param {number|string} value - The numeric value
 * @returns {string} Formatted number string (e.g., 1,500)
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '0';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return '0';
  
  return new Intl.NumberFormat('en-CA').format(numValue);
};
